import { useEffect, useState, useRef } from "react";
import { Images, X, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, uploadImage, formatApiError } from "@/lib/api";

export function MediaPicker({ onPick }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && items.length === 0) {
      setLoading(true);
      api.get("/content/media").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
    }
  }, [open]); // eslint-disable-line

  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onPick(url);
      toast.success("Image uploaded");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="mt-1.5 flex flex-wrap gap-2">
        <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted px-3 py-1.5 text-xs font-bold hover:border-[#2CACD4] transition-colors cursor-pointer" data-testid="media-picker-open">
          <Images size={14} /> Choose from library
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted px-3 py-1.5 text-xs font-bold hover:border-[#2CACD4] transition-colors cursor-pointer disabled:opacity-60" data-testid="media-picker-upload">
          {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Upload
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={doUpload} data-testid="media-picker-file" />
      </div>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-2xl rounded-3xl bg-card shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="media-picker-modal">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h3 className="font-display font-black uppercase text-lg">Media library</h3>
              <button onClick={() => setOpen(false)} className="rounded-lg p-1.5 hover:bg-muted cursor-pointer" aria-label="Close"><X size={18} /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-5">
              {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#2CACD4]" /></div>
              ) : items.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No images yet. Use Upload above or add some in Media Library.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {items.map((m) => (
                    <button key={m.id} type="button" onClick={() => { onPick(m.url); setOpen(false); }} className="group overflow-hidden rounded-xl border border-border hover:border-[#2CACD4] hover:ring-2 hover:ring-[#2CACD4]/40 transition cursor-pointer" data-testid="media-picker-item">
                      <img src={m.url} alt={m.alt} className="h-24 w-full object-cover" />
                      <p className="truncate px-2 py-1.5 text-left text-xs text-muted-foreground">{m.alt}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
